import type * as CSS from 'csstype';
import type { Component, Editor } from 'grapesjs';

import {
  wrapperClass,
  wrapperStyle,
} from '../../../components/GrapesJS/Customs/ToggleWrapper';
import { mdScreens, smScreens } from '../../../constants/grapes';
import {
  type IPropertyNumberProps,
  type IPropertySelectProps,
} from '../interfaces';

export const cmdSetDefaultRules = 'set-default-styles';

export const addVerticalAlign = (editor: Editor): void => {
  editor.StyleManager.addProperty(
    'typography',
    {
      id: 'vertical-align',
      default: '',
      label: 'Vertical align',
      options: [
        { id: 'unset', label: ' ' },
        { id: 'baseline', label: 'Baseline' },
        { id: 'bottom', label: 'Bottom' },
        { id: 'middle', label: 'Middle' },
        { id: 'sub', label: 'Sub' },
        { id: 'super', label: 'Super' },
        { id: 'text-bottom', label: 'Text bottom' },
        { id: 'text-top', label: 'Text top' },
        { id: 'top', label: 'Top' },
      ],
      property: 'vertical-align',
      type: 'select',
    } satisfies IPropertySelectProps,
    { at: 6 }
  );
};

export const addMaxMinSizes = (editor: Editor): void => {
  // remove the default properties to set a better order
  editor.StyleManager.removeProperty('dimension', 'max-width');
  editor.StyleManager.removeProperty('dimension', 'min-height');

  // ordering to properties related to width and height stay together
  editor.StyleManager.addProperty(
    'dimension',
    {
      property: 'min-width',
      type: 'number',
      min: 0,
      units: ['px', '%', 'em', 'rem', 'vh', 'vw'],
      default: 'auto',
    } satisfies IPropertyNumberProps,
    { at: 2 }
  );
  editor.StyleManager.addProperty(
    'dimension',
    {
      property: 'min-height',
      type: 'number',
      min: 0,
      units: ['px', '%', 'em', 'rem', 'vh', 'vw'],
      default: 'auto',
    } satisfies IPropertyNumberProps,
    { at: 3 }
  );
  editor.StyleManager.addProperty(
    'dimension',
    {
      property: 'max-width',
      type: 'number',
      min: 0,
      units: ['px', '%', 'em', 'rem', 'vh', 'vw'],
      default: 'auto',
    } satisfies IPropertyNumberProps,
    { at: 4 }
  );
  editor.StyleManager.addProperty(
    'dimension',
    {
      property: 'max-height',
      type: 'number',
      min: 0,
      units: ['px', '%', 'em', 'rem', 'vh', 'vw'],
      default: 'auto',
    } satisfies IPropertyNumberProps,
    { at: 5 }
  );
};

export const addOverflow = (editor: Editor): void => {
  editor.StyleManager.addProperty(
    'dimension',
    {
      property: 'overflow-x',
      type: 'select',
      default: 'visible',
      options: [
        { id: 'visible', label: 'visible' },
        { id: 'hidden', label: 'hidden' },
        { id: 'scroll', label: 'scroll' },
        { id: 'auto', label: 'auto' },
      ],
    } satisfies IPropertySelectProps,
    { at: 20 }
  );
  editor.StyleManager.addProperty(
    'dimension',
    {
      property: 'overflow-y',
      type: 'select',
      default: 'visible',
      options: [
        { id: 'visible', label: 'visible' },
        { id: 'hidden', label: 'hidden' },
        { id: 'scroll', label: 'scroll' },
        { id: 'auto', label: 'auto' },
      ],
    } satisfies IPropertySelectProps,
    { at: 21 }
  );
};

export const addFlexGap = (editor: Editor): void => {
  editor.StyleManager.addProperty('flex', {
    property: 'row-gap',
    type: 'number',
    min: 0,
    units: ['px', '%', 'em', 'rem', 'vh', 'vw'],
    default: 'auto',
  } satisfies IPropertyNumberProps);

  editor.StyleManager.addProperty('flex', {
    property: 'column-gap',
    type: 'number',
    min: 0,
    units: ['px', '%', 'em', 'rem', 'vh', 'vw'],
    default: 'auto',
  } satisfies IPropertyNumberProps);
};

export const fixColorPropertyToReadGlobalStyles = (editor: Editor): void => {
  const getCanvasDoc = () => editor.Canvas.getDocument();
  const getCanvasWindow = () => getCanvasDoc()?.defaultView ?? null;

  const getBodyColor = (): string | undefined => {
    const rule = editor.CssComposer.getRule('body');
    return rule?.getStyle()?.color as string | undefined;
  };

  const normalizeColor = (color: string): string => {
    const temp = document.createElement('div');
    temp.style.cssText = 'position:absolute;visibility:hidden;color:' + color;
    document.body.appendChild(temp);
    const normalized = window.getComputedStyle(temp).color;
    document.body.removeChild(temp);
    return normalized;
  };

  const syncSelectedColor = (component: Component | null) => {
    if (!component) return;
    if (!component.isInstanceOf('text')) return;
    setTimeout(() => {
      const win = getCanvasWindow();
      const el = component.getEl();
      if (!win || !el) return;

      const computedColor = win.getComputedStyle(el);

      const styleTargets = editor.StyleManager.getSelectedAll();
      if (styleTargets.length) {
        editor.StyleManager.select(styleTargets);
      }

      const colorProp = editor.StyleManager.getProperty('typography', 'color');
      colorProp?.upValue(computedColor.color, { noTarget: true });
    }, 0);
  };

  const hasAncestorWithExplicitColorDifferentFrom = (
    component: Component,
    normalizedReferenceColor: string
  ): boolean => {
    let parent = component.parent();
    while (parent) {
      const parentColor = parent.getStyle()?.color as string | undefined;
      if (parentColor) {
        const normalizedParent = normalizeColor(parentColor);
        if (normalizedParent !== normalizedReferenceColor) {
          return true;
        }
      }
      parent = parent.parent();
    }
    return false;
  };

  const syncAllTextsWithBody = (oldBodyColor: string) => {
    const wrapper = editor.getWrapper();
    if (!wrapper) return;

    const normalizedOld = normalizeColor(oldBodyColor);

    wrapper.find('[data-gjs-type="text"]').forEach((component) => {
      const style = component.getStyle();
      const localColor = style.color as string | undefined;
      if (!localColor) return;

      const normalizedLocal = normalizeColor(localColor);

      if (normalizedLocal === normalizedOld) {
        if (
          hasAncestorWithExplicitColorDifferentFrom(component, normalizedOld)
        ) {
          return;
        }
        const newStyle = { ...style };
        delete newStyle.color;
        component.setStyle(newStyle);
      }
    });

    const selected = editor.getSelected();
    if (selected) {
      syncSelectedColor(selected);
    }
  };

  editor.on('component:add', (component: Component) => {
    setTimeout(() => {
      if (editor.getSelected() === component) {
        syncSelectedColor(component);
      }
    }, 50);
  });

  editor.on('component:selected', (component: Component) => {
    syncSelectedColor(component);
  });

  editor.on('component:update', (component: Component) => {
    const selected = editor.getSelected();
    if (selected === component) {
      syncSelectedColor(selected);
    }
  });

  editor.on('load', () => {
    let previousBodyColor = getBodyColor();

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    editor.CssComposer.getAll().on('change:style', (rule: any) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      if (rule.selectorsToString?.() === 'body') {
        const newBodyColor = getBodyColor();

        if (
          previousBodyColor &&
          newBodyColor &&
          previousBodyColor !== newBodyColor
        ) {
          syncAllTextsWithBody(previousBodyColor);
        }

        previousBodyColor = newBodyColor;
      }
    });
  });
};

export const handlerSetDefaultRules = (
  { Commands, Css }: Editor,
  addCssContent?: string
): void => {
  const isRunning = Commands.isActive(cmdSetDefaultRules);

  if (!isRunning) {
    Commands.run(cmdSetDefaultRules);
    addCssContent && Css.addRules(addCssContent);
  }
};

export const CustomCommandsPlugin = ({ Commands }: Editor): void => {
  Commands.add(cmdSetDefaultRules, {
    // eslint-disable-next-line no-restricted-syntax
    run({ Css }) {
      Css.setRule(`.${wrapperClass}`, wrapperStyle);

      Css.setRule('.gjs-cell', {
        width: '8%',
        display: 'table-cell',
        height: '75px',
        'vertical-align': 'top',
      } satisfies CSS.PropertiesHyphen);

      Css.setRule(
        '.gjs-cell',
        {
          width: '8%',
          display: 'table-cell',
        } satisfies CSS.StandardPropertiesHyphen,
        {
          atRuleParams: `(max-width: ${mdScreens.mediaWidth})`,
          atRuleType: 'media',
        }
      );

      Css.setRule('img', {
        'max-width': '100%!important',
      } satisfies CSS.StandardPropertiesHyphen);

      Css.setRule('div[data-gjs-type="video"]', {
        'max-width': '100%!important',
        width: 'unset!important',
      } satisfies CSS.StandardPropertiesHyphen);

      Css.setRule('.mailto-link', {
        'background-color': '#00426b',
        border: '1px solid #00426b',
        color: '#ffffff',
        'border-radius': '15px',
        display: 'inline-block',
        padding: '10px 25px',
        'text-decoration': 'none',
      } satisfies CSS.StandardPropertiesHyphen);

      Css.setRule('.link-button', {
        'background-color': '#00426b',
        border: '1px solid #00426b',
        color: '#ffffff',
        'border-radius': '15px',
        display: 'inline-block',
        padding: '10px 25px',
        'text-decoration': 'none',
      } satisfies CSS.StandardPropertiesHyphen);

      Css.setRule('.link-document', {
        'background-color': '#00426b',
        border: '1px solid #00426b',
        color: '#ffffff',
        'border-radius': '15px',
        display: 'inline-block',
        padding: '10px 25px',
        'text-decoration': 'none',
      } satisfies CSS.StandardPropertiesHyphen);

      Css.setRule('.mailto-link:hover', {
        'background-color': '#07629b',
        opacity: '0.9',
      } satisfies CSS.StandardPropertiesHyphen);

      Css.setRule('.link-button:hover', {
        'background-color': '#07629b',
        opacity: '0.9',
      } satisfies CSS.StandardPropertiesHyphen);

      Css.setRule('.link-document:hover', {
        'background-color': '#07629b',
        opacity: '0.9',
      } satisfies CSS.StandardPropertiesHyphen);

      Css.setRule('.two-columns-text', {
        'column-count': '2',
        orphans: '2',
      } satisfies CSS.StandardPropertiesHyphen);

      Css.setRule(
        '.sm-two-columns-text',
        {
          'column-count': '2',
          orphans: '2',
        } satisfies CSS.StandardPropertiesHyphen,
        {
          atRuleParams: `(max-width: ${mdScreens.mediaWidth})`,
          atRuleType: 'media',
        }
      );

      Css.setRule(
        '.lg-two-columns-text',
        {
          'column-count': '2',
          orphans: '2',
        } satisfies CSS.StandardPropertiesHyphen,
        {
          atRuleParams: `(min-width: ${mdScreens.mediaWidth})`,
          atRuleType: 'media',
        }
      );

      Css.setRule('.allow-scroll', {
        'overflow-y': 'auto',
      } satisfies CSS.StandardPropertiesHyphen);

      Css.setRule(
        '.sm-allow-scroll',
        {
          'overflow-y': 'auto',
        } satisfies CSS.StandardPropertiesHyphen,
        {
          atRuleParams: `(max-height: ${smScreens.mediaHeight}) or (max-width: ${mdScreens.mediaWidth})`,
          atRuleType: 'media',
        }
      );

      Css.setRule(
        '.lg-allow-scroll',
        {
          'overflow-y': 'auto',
        } satisfies CSS.StandardPropertiesHyphen,
        {
          atRuleParams: `(min-height: ${smScreens.mediaHeight} or (min-width: ${mdScreens.mediaWidth}))`,
          atRuleType: 'media',
        }
      );

      Css.setRule('.grid-3-7-columns', {
        display: 'grid',
        'grid-template-columns': '35fr 65fr',
      } satisfies CSS.StandardPropertiesHyphen);

      Css.setRule('.grid-7-3-columns', {
        display: 'grid',
        'grid-template-columns': '65fr 35fr',
      } satisfies CSS.StandardPropertiesHyphen);
    },
  });
};
